<template>
  <div>
    <edit-h1-parcel-items
      v-if="(selectedDeclaration || selectedParcel) && declarationType === 'h1'"
      id="edit-h1-parcel-items"
      :declaration="selectedDeclaration"
      :parcel="selectedParcel"
      :shipment="shipment"
      :readonly="true"
    />
    <edit-parcel-items
      v-if="(selectedDeclaration || selectedParcel) && declarationType !== 'h1'"
      id="edit-parcel-items"
      :parcel="selectedParcel"
      :declaration="selectedDeclaration"
      :shipment="shipment"
      :readonly="true"
    />
    <status-information
      v-if="informationItem"
      :item="informationItem"
    />
    <b-row>
      <b-col class="mt-2">
        <b-card-text>
          Parcels that are held by customs. This means either additional documents or physical control are needed.
        </b-card-text>
      </b-col>
      <b-col class="text-right">
        <b-button
          variant="outline-success"
          class="m-1"
          :disabled="excelLoading"
          @click="handleExcelDownload"
        >
          Download as Excel
          <b-spinner
            v-if="excelLoading"
            small
            class="ml-1"
          />
        </b-button>
        <b-button
          :disabled="declarations.length === 0 || warehouseExcelLoading"
          variant="outline-success"
          @click="handleWarehouseExcelDownload"
        >
          Download Excel for warehouse
          <b-spinner
            v-if="warehouseExcelLoading"
            small
            class="ml-1"
          />
        </b-button>
      </b-col></b-row>
    <b-row>
      <b-col>
        <div class="overflow-x-scroll">
          <b-table
            :items="declarations"
            responsive
            :fields="fields"
            :busy="loading"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            no-local-sorting
            show-empty
            empty-text="No matching records found"
            class="mb-0"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
                <strong>Loading...</strong>
              </div>
            </template>
            <template #head()="data">
              <span class="text-nowrap">
                {{ data.label }}
              </span>
            </template>
            <template #cell(actions)="data">
              <b-button-group>
                <view-parcel-button
                  :declaration="data.item"
                  :parcel="data.item.goods_shipment || data.item.parcel"
                />
                <download-pdf-button
                  :declaration="data.item"
                  :parcel="data.item.goods_shipment || data.item.parcel"
                  :shipment="shipment"
                />
              </b-button-group>
            </template>

            <template #cell(tracking_number)="data">
              {{ (data.item.goods_shipment || data.item.parcel || {}).tracking_number }}
            </template>

            <template #cell(container_code)="data">
              {{ (data.item.goods_shipment || data.item.parcel || {}).container_code }}
            </template>

            <template #cell(descriptions)="data">
              <div>
                {{ (data.item.goods_shipment || data.item.parcel || {}).items ? truncatedDescriptions((data.item.goods_shipment || data.item.parcel || {}).items) : truncatedList((data.item.goods_shipment || data.item.parcel || {}).descriptions_preview) }}
              </div>
            </template>

            <template #cell(hs_codes)="data">
              <div>
                {{ (data.item.goods_shipment || data.item.parcel || {}).items ? (data.item.goods_shipment || data.item.parcel || {}).items.slice(0, 20).map(function (parcelItem) {return parcelItem.hs_code}).join(', ') : truncatedList((data.item.goods_shipment || data.item.parcel || {}).hs_codes_preview) }}
              </div>
            </template>

            <template #cell(value)="data">
              <div
                v-if="parcelValue(data.item) !== null"
                class="text-right"
              >
                {{ parcelValue(data.item) | formatNumber(2) }}
              </div>
            </template>

            <template #cell(status_info)="data">
              <div v-if="data.item.status_info !== null && data.item.status_info['errors']">
                <span
                  v-b-modal.modal-status-information
                  class="text-primary text-nowrap"
                  @click="informationItem = data.item"
                >
                  Show errors
                  <b-badge
                    variant="light"
                    class="text-primary"
                  >
                    {{ data.item.status_info['errors'].length }}
                  </b-badge>
                </span>
              </div>
            </template>

          </b-table>
        </div>
        <pagination
          v-show="!loading"
          :per-page="perPage"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCardText,
  BCol,
  BButton,
  BRow,
  BTable,
  BSpinner,
  BButtonGroup,
  BBadge,
} from 'bootstrap-vue'
import ViewParcelButton from '@/views/shipments/declarations/components/ViewParcelButton.vue'
import Pagination from '@/layouts/components/Pagination/Pagination.vue'
import StatusInformation from '@/views/shipments/declarations/modals/StatusInformation.vue'
import EditParcelItems from '@/views/shipments/declarations/modals/EditParcelItems.vue'
import EditH1GoodsItems from '@/views/shipments/declarations/modals/EditH1GoodsItems.vue'
import DownloadPdfButton from '@/views/shipments/declarations/components/DownloadPdfButton.vue'
import { EventBus, truncatedDescriptions, truncatedList, resolveGoodsShipmentValue } from '@core/utils/utils'
import { searchQuery } from '@core/utils/filter'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'

export default {
  components: {
    DownloadPdfButton,
    BButtonGroup,
    EditParcelItems,
    EditH1ParcelItems: EditH1GoodsItems,
    StatusInformation,
    ViewParcelButton,
    BCardText,
    BCol,
    BRow,
    BButton,
    BTable,
    BSpinner,
    BBadge,
    Pagination,
  },
  mixins: [excelDownloadMixin],
  props: {
    shipment: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => {},
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
  },
  data() {
    return {
      loading: false,
      informationItem: null,
      page: 1,
      perPage: 100,
      selectedDeclaration: null,
      selectedParcel: null,
      sortBy: null,
      isSortDirDesc: null,
      fields: [
        { key: 'actions', label: 'Actions' },
        { key: 'tracking_number', label: 'Tracking code', sortable: true },
        { key: 'container_code', label: 'Container', sortable: true },
        { key: 'descriptions', label: 'Description' },
        { key: 'hs_codes', label: 'HS code' },
        { key: 'value', label: 'Value' },
        { key: 'lrn', label: 'LRN' },
        { key: 'mrn', label: 'MRN' },
        { key: 'status_info', label: 'Status Information' },
      ],
      declarations: [],
    }
  },
  computed: {
    url() {
      let url = `/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipment.id}/declarations/${this.declarationType}/held-by-customs`
      url += `?page=${this.page}`
      url += `&per_page=${this.perPage}`
      return url
    },
  },
  watch: {
    filters() {
      this.fetchData()
    },
    sortBy() {
      this.fetchData()
    },
    isSortDirDesc() {
      this.fetchData()
    },
  },
  created() {
    EventBus.$on('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$on('set-view-parcel', this.viewParcel)
    EventBus.$on('renewDeclarationList', this.handleRenewDeclarationList)
    this.fetchData()
  },
  mounted() {
    EventBus.$on('pageChange', this.handlePageChange)
  },
  beforeDestroy() {
    EventBus.$off('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$off('set-view-parcel', this.viewParcel)
    EventBus.$off('renewDeclarationList', this.handleRenewDeclarationList)
    EventBus.$off('pageChange', this.handlePageChange)
  },
  methods: {
    handleUpdateParcelItems() {
      this.selectedDeclaration = null
      this.selectedParcel = null
      this.fetchData()
      EventBus.$emit('refresh-statistics')
    },
    viewParcel(declaration, parcel) {
      this.selectedDeclaration = declaration
      this.selectedParcel = parcel
      this.$nextTick(() => {
        this.$bvModal.show(this.declarationType === 'h1' ? 'edit-h1-parcel-items' : 'edit-parcel-items')
      })
    },
    handleRenewDeclarationList() {
      this.fetchData()
      EventBus.$emit('refresh-statistics')
    },
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    truncatedDescriptions,
    truncatedList,
    parcelValue(item) {
      const goodsShipment = item.goods_shipment || item.parcel || {}
      return resolveGoodsShipmentValue(goodsShipment)
    },
    fetchData() {
      if (!this.loading) {
        this.loading = true
        this.$http.get(searchQuery(this.url, this.filters), {
          params: {
            sortBy: this.sortBy,
            sortDesc: this.isSortDirDesc,
          },
        })
          .then(response => {
            this.loading = false
            this.declarations = response.data.data
            EventBus.$emit('updateMeta', response.data.meta)
          })
      }
    },
    downloadUrl() {
      return `${process.env.VUE_APP_API_URL}/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipment.id}/declarations/${this.declarationType}/heldByCustoms`
    },
    excelDownloadUrl() {
      return `${this.downloadUrl}/excel`
    },
    warehouseExcelUrl() {
      return `${this.downloadUrl}/excel-for-warehouse`
    },
  },
}
</script>
